import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import UserEditModal from './UserEditModal';
import UserCreateModal from './UserCreateModal';
import UserDeleteModal from './UserDeleteModal';
import { userService } from '../../../services/userService';

const UserManagement = ({ onStatsChange }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  useEffect(() => {
    loadUsers();
  }, [pagination?.page, searchTerm, roleFilter, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const result = await userService?.getUsers({
        page: pagination?.page,
        limit: pagination?.limit,
        search: searchTerm,
        role: roleFilter === 'all' ? undefined : roleFilter,
        status: statusFilter === 'all' ? undefined : statusFilter === 'active'
      });
      
      setUsers(result?.data || []);
      setPagination(prev => ({
        ...prev,
        total: result?.total || 0
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (type, value) => {
    if (type === 'role') setRoleFilter(value);
    if (type === 'status') setStatusFilter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSelectUser = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev?.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedUsers(users?.map(user => user?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = (user) => {
    setDeletingUser(user);
    setShowDeleteModal(true);
  };

  const handleUserUpdate = (updatedUser) => {
    setUsers(prev => prev?.map(user => 
      user?.id === updatedUser?.id ? updatedUser : user
    ));
    setShowEditModal(false);
    setEditingUser(null);
    onStatsChange();
  };

  const handleUserCreate = (newUser) => {
    loadUsers(); // Reload the list
    setShowCreateModal(false);
    onStatsChange();
  };

  const handleUserDelete = (deletedUserId) => {
    setUsers(prev => prev?.filter(user => user?.id !== deletedUserId));
    setShowDeleteModal(false);
    setDeletingUser(null);
    onStatsChange();
  };

  const handleBulkAction = async (action) => {
    try {
      if (action === 'activate') {
        await userService?.bulkUpdateStatus(selectedUsers, true);
      } else if (action === 'deactivate') {
        await userService?.bulkUpdateStatus(selectedUsers, false);
      }
      setSelectedUsers([]);
      loadUsers();
      onStatsChange();
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-destructive/10 text-destructive';
      case 'premium_member': return 'bg-warning/10 text-warning';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'gold': return 'bg-warning/10 text-warning';
      case 'premium': return 'bg-accent/10 text-accent';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' ? user?.is_active : !user?.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const totalPages = Math.ceil(pagination?.total / pagination?.limit);

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-foreground">Users</h2>
          <span className="px-2 py-1 bg-muted rounded-full text-sm text-muted-foreground">
            {pagination?.total} total
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {selectedUsers?.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedUsers?.length} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('activate')}
              >
                Activate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('deactivate')}
              >
                Deactivate
              </Button>
            </div>
          )}
          
          <Button onClick={() => setShowCreateModal(true)}>
            <Icon name="Plus" size={16} className="mr-2" />
            Add User
          </Button>
        </div>
      </div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => handleSearch(e?.target?.value)}
            leftIcon="Search"
          />
        </div>
        
        <div className="flex gap-2">
          <Select
            value={roleFilter}
            onChange={(value) => handleFilterChange('role', value)}
            options={[
              { value: 'all', label: 'All Roles' },
              { value: 'admin', label: 'Admin' },
              { value: 'premium_member', label: 'Premium Member' },
              { value: 'member', label: 'Member' }
            ]}
          />
          
          <Select
            value={statusFilter}
            onChange={(value) => handleFilterChange('status', value)}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]}
          />
        </div>
      </div>
      {/* Users Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Loading users...</p>
          </div>
        ) : filteredUsers?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Users" className="text-muted-foreground mx-auto mb-4" size={48} />
            <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
            <p className="text-muted-foreground">
              {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating a new user'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedUsers?.length === filteredUsers?.length}
                      onChange={(e) => handleSelectAll(e?.target?.checked)}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Membership
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {filteredUsers?.map((user) => (
                  <tr key={user?.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers?.includes(user?.id)}
                        onChange={(e) => handleSelectUser(user?.id, e?.target?.checked)}
                        className="rounded border-border text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                          {user?.avatar_url ? (
                            <img 
                              src={user?.avatar_url} 
                              alt={user?.full_name} 
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <Icon name="User" size={20} className="text-muted-foreground" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-foreground">{user?.full_name}</div>
                          <div className="text-sm text-muted-foreground">{user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user?.role)}`}>
                        {user?.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierBadgeColor(user?.membership_tier)}`}>
                        {user?.membership_tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user?.is_active ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
                      }`}>
                        {user?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(user.created_at)?.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-primary hover:text-primary/80 p-1 rounded hover:bg-muted"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-destructive hover:text-destructive/80 p-1 rounded hover:bg-muted"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((pagination?.page - 1) * pagination?.limit) + 1} to {Math.min(pagination?.page * pagination?.limit, pagination?.total)} of {pagination?.total} results
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev?.page - 1 }))}
              disabled={pagination?.page === 1}
            >
              Previous
            </Button>
            
            <span className="px-3 py-1 text-sm text-foreground">
              Page {pagination?.page} of {totalPages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev?.page + 1 }))}
              disabled={pagination?.page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {/* Modals */}
      {showEditModal && editingUser && (
        <UserEditModal
          user={editingUser}
          onSave={handleUserUpdate}
          onCancel={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
        />
      )}
      {showCreateModal && (
        <UserCreateModal
          onSave={handleUserCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      )}
      {showDeleteModal && deletingUser && (
        <UserDeleteModal
          user={deletingUser}
          onConfirm={handleUserDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeletingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;